import Button from "../../components/base/Button";
import { useState } from "react";
import {
  useSEO,
  generateProductSchema,
  generateWebPageSchema,
} from "../../utils/seo";

export default function MoveCourier() {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isCalendlyModalOpen, setIsCalendlyModalOpen] = useState(false);

  useSEO({
    title:
      "MoveCourier - Rastreamento e gestão de entregas em tempo real | Moveideias",
    description:
      "Solução tecnológica de logística que otimiza rotas, garante rastreamento e comprovação digital de entregas em tempo real com total eficiência e transparência.",
    keywords:
      "MoveCourier, rastreamento, entregas, logística, GPS, comprovação digital, gestão de frota, transporte",
    canonical: "/movecourier",
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        generateProductSchema({
          name: "MoveCourier",
          description:
            "Solução tecnológica de logística que otimiza rotas, garante rastreamento e comprovação digital de entregas em tempo real.",
          url: "/movecourier",
        }),
        generateWebPageSchema({
          name: "MoveCourier - Rastreamento e Gestão de Entregas",
          description:
            "Controle total das operações logísticas com rastreamento em tempo real.",
          url: "/movecourier",
        }),
      ],
    },
  });

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative min-h-screen flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(75, 181, 171, 0.85), rgba(75, 181, 171, 0.85)), url('/images/products/movecourier/hero-background.jpeg')`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center w-full">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              MoveCourier: controle total das suas operações logísticas
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed">
              Plataforma completa para gestão em tempo real de coletas e
              entregas, com rastreabilidade, comprovação digital e tratativas de
              inconformidades.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                onClick={() => setIsCalendlyModalOpen(true)}
                className="bg-[#f28c1a] text-white hover:bg-[#cc7013] transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                Solicitar demonstração
              </Button>
              <Button
                size="lg"
                onClick={() => setIsContactModalOpen(true)}
                className="bg-[#f28c1a] text-white hover:bg-[#cc7013] transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                Fale conosco
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Principais Benefícios */}
      <section
        className="py-20 bg-cover bg-center relative"
        style={{
          backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.95)), url('/images/backgrounds/movecourier-features.jpg')`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#221a32] mb-6">
              Controle total das suas operações de campo
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              O MoveCourier digitaliza, monitora e valida cada passo da execução
              logística, conectando o campo à gestão com total controle e
              evidência.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div
              className="bg-white/90 backdrop-blur-sm p-8 rounded-xl text-center shadow-lg border border-teal-100 bg-cover bg-center relative"
              style={{
                backgroundImage: `linear-gradient(rgba(231, 234, 246, 0.9), rgba(231, 234, 246, 0.9)), url('/images/products/movecourier/controle-tempo-real-bg.jpg')`,
              }}
            >
              <div className="w-16 h-16 bg-[#4bb5ab] rounded-full flex items-center justify-center mx-auto mb-4 relative z-10">
                <i className="ri-eye-line text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#221a32] mb-3 relative z-10">
                Controle em tempo real
              </h3>
              <p className="text-gray-600 relative z-10">
                Acompanhe a movimentação da equipe em campo com geolocalização
                em tempo real e rastreabilidade total das entregas.
              </p>
            </div>

            <div
              className="bg-white/90 backdrop-blur-sm p-8 rounded-xl text-center shadow-lg border border-teal-100 bg-cover bg-center relative"
              style={{
                backgroundImage: `linear-gradient(rgba(231, 234, 246, 0.9), rgba(231, 234, 246, 0.9)), url('/images/products/movecourier/evidencias-digitais-bg.jpg')`,
              }}
            >
              <div className="w-16 h-16 bg-[#4bb5ab] rounded-full flex items-center justify-center mx-auto mb-4 relative z-10">
                <i className="ri-camera-line text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#221a32] mb-3 relative z-10">
                Evidências digitais
              </h3>
              <p className="text-gray-600 relative z-10">
                Capture fotos, assinaturas eletrônicas e registros automáticos
                que comprovam a execução das atividades.
              </p>
            </div>

            <div
              className="bg-white/90 backdrop-blur-sm p-8 rounded-xl text-center shadow-lg border border-teal-100 bg-cover bg-center relative"
              style={{
                backgroundImage: `linear-gradient(rgba(231, 234, 246, 0.9), rgba(231, 234, 246, 0.9)), url('/images/products/movecourier/comunicacao-eficiente-bg.jpg')`,
              }}
            >
              <div className="w-16 h-16 bg-[#4bb5ab] rounded-full flex items-center justify-center mx-auto mb-4 relative z-10">
                <i className="ri-message-2-line text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#221a32] mb-3 relative z-10">
                Comunicação eficiente
              </h3>
              <p className="text-gray-600 relative z-10">
                Canal direto e centralizado entre a base e os profissionais de
                campo, reduzindo erros operacionais.
              </p>
            </div>

            <div
              className="bg-white/90 backdrop-blur-sm p-8 rounded-xl text-center shadow-lg border border-teal-100 bg-cover bg-center relative"
              style={{
                backgroundImage: `linear-gradient(rgba(231, 234, 246, 0.9), rgba(231, 234, 246, 0.9)), url('/images/products/movecourier/gestao-de-incidentes-bg.jpg')`,
              }}
            >
              <div className="w-16 h-16 bg-[#4bb5ab] rounded-full flex items-center justify-center mx-auto mb-4 relative z-10">
                <i className="ri-alert-line text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#221a32] mb-3 relative z-10">
                Gestão de incidentes
              </h3>
              <p className="text-gray-600 relative z-10">
                Detecte inconformidades em tempo real e antecipe ações
                corretivas antes que causem impactos maiores.
              </p>
            </div>

            <div
              className="bg-white/90 backdrop-blur-sm p-8 rounded-xl text-center shadow-lg border border-teal-100 bg-cover bg-center relative"
              style={{
                backgroundImage: `linear-gradient(rgba(231, 234, 246, 0.9), rgba(231, 234, 246, 0.9)), url('/images/products/movecourier/eficiencia-operacional-bg.jpg')`,
              }}
            >
              <div className="w-16 h-16 bg-[#4bb5ab] rounded-full flex items-center justify-center mx-auto mb-4 relative z-10">
                <i className="ri-route-line text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#221a32] mb-3 relative z-10">
                Eficiência operacional
              </h3>
              <p className="text-gray-600 relative z-10">
                Planeje roteiros otimizados e analise o desempenho logístico
                para redução de custos operacionais.
              </p>
            </div>

            <div
              className="bg-white/90 backdrop-blur-sm p-8 rounded-xl text-center shadow-lg border border-teal-100 bg-cover bg-center relative"
              style={{
                backgroundImage: `linear-gradient(rgba(231, 234, 246, 0.9), rgba(231, 234, 246, 0.9)), url('/images/products/movecourier/decisoes-baseadas-em-dados-bg.jpg')`,
              }}
            >
              <div className="w-16 h-16 bg-[#4bb5ab] rounded-full flex items-center justify-center mx-auto mb-4 relative z-10">
                <i className="ri-bar-chart-line text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#221a32] mb-3 relative z-10">
                Decisões baseadas em dados
              </h3>
              <p className="text-gray-600 relative z-10">
                Geração de informações organizadas e registros históricos que
                apoiam decisões estratégicas.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Problemas que Resolve */}
      <section
        className="py-20 bg-cover bg-center relative"
        style={{
          backgroundImage: `linear-gradient(rgba(231, 234, 246, 0.95), rgba(231, 234, 246, 0.95)), url('/images/backgrounds/movecourier-problems.jpg')`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#221a32] mb-6">
              Problemas específicos que o MoveCourier resolve
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Transforme os principais desafios operacionais em vantagens
              competitivas.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-close-line text-xl text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-red-600 mb-2">
                    PROBLEMA
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Falta de rastreabilidade das entregas e visibilidade sobre
                    localização de veículos
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4 mt-4">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-xl text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-green-600 mb-2">
                    SOLUÇÃO
                  </h3>
                  <p className="text-gray-600">
                    Geolocalização em tempo real e visibilidade total das
                    operações
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-close-line text-xl text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-red-600 mb-2">
                    PROBLEMA
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Ausência de comprovação das coletas/entregas gerando
                    disputas
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4 mt-4">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-xl text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-green-600 mb-2">
                    SOLUÇÃO
                  </h3>
                  <p className="text-gray-600">
                    Fotos, assinaturas e eventos capturados no app como
                    evidência digital
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white space-y-8 p-8 rounded-xl shadow-lg border border-gray-100">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-close-line text-xl text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-red-600 mb-2">
                    PROBLEMA
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Comunicação desestruturada com equipes via telefone
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4 mt-4">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-xl text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-green-600 mb-2">
                    SOLUÇÃO
                  </h3>
                  <p className="text-gray-600">
                    Canal direto e integrado entre gestor e operador
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-close-line text-xl text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-red-600 mb-2">
                    PROBLEMA
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Altos custos operacionais com transporte e falhas na
                    execução
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4 mt-4">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-xl text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-green-600 mb-2">
                    SOLUÇÃO
                  </h3>
                  <p className="text-gray-600">
                    Redução de ineficiências e retrabalhos, entregas mais
                    econômicas
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Resultados Comprovados */}
      <section
        className="py-20 bg-cover bg-center relative"
        style={{
          backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.95)), url('/images/backgrounds/movecourier-results.jpg')`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#221a32] mb-6">
              Resultados comprovados
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-[#4bb5ab] rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-eye-line text-xl text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#221a32] mb-3">
                  100% de visibilidade operacional
                </h3>
                <p className="text-gray-600">
                  Controle total das operações com rastreamento em tempo real de
                  toda a frota.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-[#4bb5ab] rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-check-line text-xl text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#221a32] mb-3">
                  Redução de 90% de ruídos com as entregas
                </h3>
                <p className="text-gray-600">
                  Evidências digitais eliminam questionamentos sobre entregas
                  realizadas.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-[#4bb5ab] rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-time-line text-xl text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#221a32] mb-3">
                  Resposta 80% mais rápida
                </h3>
                <p className="text-gray-500">
                  Identificação e correção imediata de incidentes operacionais.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-[#4bb5ab] rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-customer-service-line text-xl text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#221a32] mb-3">
                  95% de satisfação do cliente
                </h3>
                <p className="text-gray-600">
                  Informações precisas e atualizações em tempo real aumentam a
                  confiança.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Integração com TeuPedido */}
      <section
        className="py-20 bg-cover bg-center relative"
        style={{
          backgroundImage: `linear-gradient(rgba(231, 234, 246, 0.95), rgba(231, 234, 246, 0.95)), url('/images/backgrounds/integration-teupedido.jpg')`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#221a32] mb-6">
              Integração completa com TeuPedido
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Quando utilizado em conjunto com o TeuPedido, o MoveCourier
              enriquece a etapa de transporte com dados operacionais em tempo
              real, garantindo rastreabilidade de ponta a ponta no ciclo
              logístico.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 text-center">
              <div className="w-16 h-16 bg-[#ea5836] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shopping-cart-line text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#221a32] mb-4">
                TeuPedido
              </h3>
              <p className="text-gray-600">
                Gestão completa de pedidos desde a criação até a preparação para
                entrega.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 text-center">
              <div className="w-16 h-16 bg-[#4bb5ab] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-truck-line text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#221a32] mb-4">
                MoveCourier
              </h3>
              <p className="text-gray-600">
                Execução e monitoramento da entrega com controle total da
                operação logística.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 text-center">
              <div className="w-16 h-16 bg-[#f28c1a] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-check-double-line text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#221a32] mb-4">
                Resultado
              </h3>
              <p className="text-gray-600">
                Visibilidade unificada do ciclo completo de pedidos com
                rastreabilidade total.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section
        className="py-20 bg-cover bg-center relative"
        style={{
          backgroundImage: `linear-gradient(rgba(75, 181, 171, 0.9), rgba(75, 181, 171, 0.9)), url('/images/backgrounds/movecourier-cta.jpg')`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Transforme sua operação logística
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Descubra como o MoveCourier pode digitalizar, monitorar e otimizar
            suas operações de coleta e entrega com controle total e evidências
            digitais.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => setIsCalendlyModalOpen(true)}
              className="bg-[#f28c1a] text-white hover:bg-[#cc7013] transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Solicitar demonstração
            </Button>
            <Button
              size="lg"
              onClick={() => setIsContactModalOpen(true)}
              className="bg-[#f28c1a] text-white hover:bg-[#cc7013] transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Fale conosco
            </Button>
          </div>
        </div>
      </section>

      {/* Modal de Contato */}
      {isContactModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-2xl font-bold text-[#221a32]">
                Fale conosco
              </h3>
              <button
                onClick={() => setIsContactModalOpen(false)}
                className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors duration-200 cursor-pointer"
              >
                <i className="ri-close-line text-2xl" />
              </button>
            </div>
            <div className="h-[600px]">
              <iframe
                src="https://portal.moveideias.com/workflow/public/form/9ec92cd7-ac11-4a04-8a8a-6d71c473e303"
                className="w-full h-full"
                title="Formulário de contato"
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal do Calendly */}
      {isCalendlyModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-2xl font-bold text-[#221a32]">
                Agendar demonstração
              </h3>
              <button
                onClick={() => setIsCalendlyModalOpen(false)}
                className="w-10 h-10 flex items-center justify-center 
                text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full  transition-colors duration-200 cursor-pointer"
              >
                <i className="ri-close-line text-2xl" />
              </button>
            </div>
            <div className="h-[600px]">
              <iframe
                src="https://calendly.com/move-comercial/60min"
                className="w-full h-full"
                title="Agendar demonstração"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
